import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type CashFlowStatement,
  downloadCashFlowXlsx,
  getCashFlow,
  getInventoryValuationReport,
  type InventoryValuationReport,
  type ReportBasis,
} from "@/lib/api";
import { ReportBackButton } from "./ReportBackButton";
import { ReportError } from "./StatementTables";

// Cash-flow statement page for
// GET /finance/invoicing/reports/cash-flow (all amounts are integer
// minor units — cents — never floats). Reimplemented for this app; the
// upstream dubbl cash-flow page is layout reference only and must not be
// copied (see docs/legal/upstream-frontends.md).

// The endpoint returns no currency field, so display in the organization
// default. Every other report page reads currency off its response; if the
// handler ever gains one, read it from there instead of this constant.
const DISPLAY_CURRENCY = "USD";

type CashFlowMethod = "indirect" | "direct";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function janFirstIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function MoneyRow({
  label,
  cents,
  bold,
}: {
  label: string;
  cents: number;
  bold?: boolean;
}) {
  return (
    <TableRow>
      <TableCell className={bold ? "font-semibold" : ""}>{label}</TableCell>
      <TableCell
        className={`text-right tabular-nums ${bold ? "font-semibold" : ""}`}
      >
        {money(cents)}
      </TableCell>
    </TableRow>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="surface overflow-hidden rounded-2xl">
      <h2 className="bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
        {title}
      </h2>
      {children}
    </section>
  );
}

interface CashFlowQuery {
  start: string;
  end: string;
  method: CashFlowMethod;
  basis: ReportBasis;
}

export function CashFlowPage() {
  const [start, setStart] = useState(janFirstIso);
  const [end, setEnd] = useState(todayIso);
  const [method, setMethod] = useState<CashFlowMethod>("indirect");
  const [basis, setBasis] = useState<ReportBasis>("accrual");
  const [applied, setApplied] = useState<CashFlowQuery>({
    start: janFirstIso(),
    end: todayIso(),
    method: "indirect",
    basis: "accrual",
  });
  const [statement, setStatement] = useState<CashFlowStatement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Live Operations (Inventory) snapshot — separate from the GL working-
  // capital "Inventory change" row above. Not every organization has the
  // Inventory crate installed, so a failed/forbidden fetch gets its own
  // failed-source card (with Retry) instead of the card silently vanishing.
  const [valuation, setValuation] = useState<InventoryValuationReport | null>(
    null,
  );
  const [valuationError, setValuationError] = useState<string | null>(null);
  const [valuationLoading, setValuationLoading] = useState(true);

  const run = useCallback((query: CashFlowQuery) => {
    setLoading(true);
    setError(null);
    getCashFlow({
      start_date: query.start,
      end_date: query.end,
      method: query.method,
      basis: query.basis,
    })
      .then(setStatement)
      .catch((cause: unknown) => {
        // A failed report read is its own state: never keep or zero the
        // numbers, show the error with Retry instead.
        setStatement(null);
        setError(toErrorMessage(cause));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const loadValuation = useCallback(() => {
    setValuationLoading(true);
    getInventoryValuationReport()
      .then((next) => {
        setValuation(next);
        setValuationError(null);
      })
      .catch((cause: unknown) => {
        setValuation(null);
        setValuationError(toErrorMessage(cause));
      })
      .finally(() => setValuationLoading(false));
  }, []);

  useEffect(() => {
    loadValuation();
  }, [loadValuation]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end, method, basis });
  };

  const exportXlsx = async () => {
    // Dedicated cash-flow workbook twin (`GET /reports/cash-flow.xlsx`) —
    // the applied range, method, and basis pass straight through.
    setExporting(true);
    setError(null);
    try {
      await downloadCashFlowXlsx({
        start_date: applied.start,
        end_date: applied.end,
        method: applied.method,
        basis: applied.basis,
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setExporting(false);
    }
  };

  const reconciliation = statement?.reconciliation;

  return (
    <div className="grid gap-6">
      <div>
        <PageHeader
          title="Cash flow"
          description="Operating, investing, and financing activity for a date range, with a reconciliation check that the computed movement matches the cash accounts."
          actions={<ReportBackButton />}
        />
        {statement ? (
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {statement.start_date} – {statement.end_date} · {statement.method} ·{" "}
            {statement.basis} · {DISPLAY_CURRENCY}
          </p>
        ) : null}
      </div>

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <form
          onSubmit={submit}
          className="surface flex min-w-0 flex-1 flex-wrap items-end gap-3 rounded-2xl p-5"
        >
          <label htmlFor="cf-start" className="grid gap-1 text-xs font-medium">
            Start date
            <Input
              id="cf-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <label htmlFor="cf-end" className="grid gap-1 text-xs font-medium">
            End date
            <Input
              id="cf-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
              disabled={loading}
            />
          </label>
          <fieldset className="grid gap-1 text-xs font-medium">
            <legend>Method</legend>
            <div
              role="radiogroup"
              aria-label="Cash-flow method"
              className="flex gap-1 rounded-xl border border-[var(--border)] p-1"
            >
              {(["indirect", "direct"] as const).map((option) => (
                <label key={option}>
                  <input
                    type="radio"
                    name="cf-method"
                    value={option}
                    checked={method === option}
                    onChange={() => setMethod(option)}
                    disabled={loading}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={
                      "block cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium capitalize transition " +
                      (method === option
                        ? "bg-[var(--action)] text-[var(--action-foreground)]"
                        : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]")
                    }
                  >
                    {option}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <label htmlFor="cf-basis" className="grid gap-1 text-xs font-medium">
            Basis
            <select
              id="cf-basis"
              className="select"
              value={basis}
              onChange={(e) => setBasis(e.target.value as ReportBasis)}
              disabled={loading}
            >
              <option value="accrual">Accrual</option>
              <option value="cash">Cash</option>
            </select>
          </label>
          <Button type="submit" disabled={loading || !start || !end}>
            {loading ? "Running..." : "Run report"}
          </Button>
          <p className="w-full text-xs text-[var(--muted-foreground)]">
            This statement is reconciled on a cash basis: the net movement it
            shows is checked against the actual change in your cash accounts
            for the same period. If the Reconciliation section below shows a
            red alert, those two figures do not match — the difference is
            listed there so you can look into it before relying on the
            numbers.
          </p>
        </form>
        <span
          className="ms-auto"
          title="Download the cash-flow workbook (.xlsx) for the applied range"
        >
          <Button
            type="button"
            variant="secondary"
            onClick={exportXlsx}
            disabled={exporting || loading}
          >
            {exporting ? "Exporting..." : "Export (XLSX)"}
          </Button>
        </span>
      </div>

      {loading && statement === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : statement ? (
        <div className="grid gap-4">
          <SectionCard title="Cash movement">
            <Table>
              <TableBody>
                <MoneyRow
                  label="Opening cash balance"
                  cents={statement.opening_cash_balance_cents}
                />
                <MoneyRow
                  label="Net cash change"
                  cents={statement.net_cash_change_cents}
                />
                <MoneyRow
                  label="Closing cash balance"
                  cents={statement.closing_cash_balance_cents}
                  bold
                />
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="Operating activities">
            <Table>
              <TableBody>
                <MoneyRow
                  label="Net income"
                  cents={statement.operating_activities.net_income_cents}
                />
                <MoneyRow
                  label="Depreciation"
                  cents={statement.operating_activities.depreciation_cents}
                />
                <MoneyRow
                  label="Accounts receivable change"
                  cents={
                    statement.operating_activities.working_capital_changes
                      .accounts_receivable_cents
                  }
                />
                <MoneyRow
                  label="Accounts payable change"
                  cents={
                    statement.operating_activities.working_capital_changes
                      .accounts_payable_cents
                  }
                />
                <MoneyRow
                  label="Inventory change"
                  cents={
                    statement.operating_activities.working_capital_changes
                      .inventory_cents
                  }
                />
                <MoneyRow
                  label="Other operating movements"
                  cents={
                    statement.operating_activities.working_capital_changes
                      .other_cents
                  }
                />
                <MoneyRow
                  label="Total operating"
                  cents={statement.operating_activities.total_cents}
                  bold
                />
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="Investing activities">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statement.investing_activities.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      No investing activity in this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  statement.investing_activities.items.map((item) => (
                    <TableRow key={item.account_id}>
                      <TableCell>
                        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                          {item.code}
                        </span>
                        {item.name}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(item.amount_cents)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                <MoneyRow
                  label="Total investing"
                  cents={statement.investing_activities.total_cents}
                  bold
                />
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="Financing activities">
            <Table>
              <TableBody>
                <MoneyRow
                  label="Loan payments"
                  cents={statement.financing_activities.loan_payments_cents}
                />
                <MoneyRow
                  label="Equity changes"
                  cents={statement.financing_activities.equity_changes_cents}
                />
              </TableBody>
            </Table>
            {statement.financing_activities.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.financing_activities.items.map((item) => (
                    <TableRow key={item.account_id}>
                      <TableCell>
                        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                          {item.code}
                        </span>
                        {item.name}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {money(item.amount_cents)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
            <Table>
              <TableBody>
                <MoneyRow
                  label="Total financing"
                  cents={statement.financing_activities.total_cents}
                  bold
                />
              </TableBody>
            </Table>
          </SectionCard>

          {reconciliation ? (
            <section
              aria-label="Reconciliation"
              className="surface overflow-hidden rounded-2xl"
            >
              <h2 className="bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest text-[var(--muted-foreground)] uppercase">
                Reconciliation
              </h2>
              <Table>
                <TableBody>
                  <MoneyRow
                    label="Computed net change"
                    cents={reconciliation.computed_net_change_cents}
                  />
                  <MoneyRow
                    label="Cash account movement"
                    cents={reconciliation.cash_account_movement_cents}
                  />
                  <MoneyRow
                    label="Difference"
                    cents={reconciliation.difference_cents}
                    bold
                  />
                </TableBody>
              </Table>
              <p
                role={reconciliation.balanced ? undefined : "alert"}
                className={`px-5 py-3 text-sm ${reconciliation.balanced ? "text-[var(--muted-foreground)]" : "font-semibold text-[var(--danger)]"}`}
              >
                {reconciliation.balanced
                  ? "Computed movement matches the cash accounts — reconciled."
                  : "Computed movement does NOT match the cash accounts — out of balance."}
              </p>
            </section>
          ) : null}

          {valuationError ? (
            <SectionCard title="Inventory valuation (live, from Operations)">
              <div role="alert" className="grid gap-3 px-5 py-4 text-sm">
                <p className="font-semibold text-[var(--danger)]">
                  The live inventory valuation could not be loaded from
                  Operations. The cash-flow statement above does not depend on
                  it.
                </p>
                <p className="text-[var(--muted-foreground)]">
                  {valuationError}
                </p>
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={loadValuation}
                    disabled={valuationLoading}
                  >
                    {valuationLoading ? "Retrying…" : "Retry"}
                  </Button>
                </div>
              </div>
            </SectionCard>
          ) : valuation && valuation.skus.length > 0 ? (
            <SectionCard title="Inventory valuation (live, from Operations)">
              <div className="hidden min-[64rem]:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-right">On hand</TableHead>
                      <TableHead className="text-right">Avg-cost value</TableHead>
                      <TableHead className="text-right">FIFO value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {valuation.skus.map((sku) => (
                      <TableRow key={sku.sku_id}>
                        <TableCell>
                          <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                            {sku.code}
                          </span>
                          {sku.name}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {sku.on_hand_units}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {sku.valued && sku.average_value_cents !== null
                            ? money(sku.average_value_cents)
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {sku.valued && sku.fifo_value_cents !== null
                            ? money(sku.fifo_value_cents)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <MoneyRow
                      label="Total (avg-cost)"
                      cents={valuation.total_average_value_cents}
                      bold
                    />
                    <MoneyRow
                      label="Total (FIFO)"
                      cents={valuation.total_fifo_value_cents}
                      bold
                    />
                  </TableBody>
                </Table>
              </div>
              <div className="stack-list grid gap-3 p-4 min-[64rem]:hidden">
                {valuation.skus.map((sku) => (
                  <article
                    key={sku.sku_id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <span className="font-medium">
                        <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                          {sku.code}
                        </span>
                        {sku.name}
                      </span>
                      <span className="tabular-nums">
                        {sku.on_hand_units} on hand
                      </span>
                    </div>
                    <CardField label="Avg-cost value">
                      {sku.valued && sku.average_value_cents !== null
                        ? money(sku.average_value_cents)
                        : "—"}
                    </CardField>
                    <CardField label="FIFO value">
                      {sku.valued && sku.fifo_value_cents !== null
                        ? money(sku.fifo_value_cents)
                        : "—"}
                    </CardField>
                  </article>
                ))}
                <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4 text-sm font-semibold">
                    <span>Total (avg-cost)</span>
                    <span className="tabular-nums">
                      {money(valuation.total_average_value_cents)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-4 pt-2 text-sm font-semibold">
                    <span>Total (FIFO)</span>
                    <span className="tabular-nums">
                      {money(valuation.total_fifo_value_cents)}
                    </span>
                  </div>
                </article>
              </div>
              <p className="px-5 py-3 text-sm text-[var(--muted-foreground)]">
                On-hand stock valuation from Operations, as of now. This does
                not post to the ledger; the "Inventory change" row above is
                this statement's own working-capital movement.
              </p>
            </SectionCard>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
